// client/src/pages/admin/AdminAnnouncements.js
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../services/api';
import axios from 'axios';
import { Megaphone, Send, Users, Clock, CheckCircle, Eye } from 'lucide-react';
import ConfirmModal from '../../components/common/ConfirmModal';

const AdminAnnouncements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetRole, setTargetRole] = useState('all');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const [history, setHistory] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') navigate('/admin/login');
  }, [user, navigate]);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/platform/announcements/recent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.announcements || []);
    } catch (err) { console.error(err); }
    finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;
    setConfirmModal({
      title: 'Send Announcement',
      message: `Send announcement to ${targetRole === 'all' ? 'ALL users' : targetRole + 's'}?`,
      icon: 'info',
      confirmLabel: 'Send',
      confirmColor: 'blue',
      onConfirm: async () => {
        setConfirmModal(null);
        setSending(true);
        setSent(null);
        try {
          const token = localStorage.getItem('token');
          const res = await axios.post(`${API_URL}/admin/platform/announcements`,
            { title: title.trim(), message: message.trim(), targetRole },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSent({ count: res.data.sent, role: res.data.targetRole });
          setTitle('');
          setMessage('');
          fetchHistory();
        } catch (err) {
          toast.error('Failed to send announcement');
        } finally {
          setSending(false);
        }
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-blue-500" /> Announcements
            </h1>
            <p className="text-sm text-gray-500 mt-1">Broadcast notifications to platform users</p>
          </div>

          {/* Send Form */}
          <div className="bg-white rounded-lg border p-6 mb-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">New Announcement</h2>
            <form onSubmit={handleSend} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input
                  type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Platform Maintenance, New Feature, Welcome..."
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  maxLength={100} required
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Message</label>
                <textarea
                  value={message} onChange={(e) => setMessage(e.target.value)}
                  placeholder="Write the announcement message..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                  maxLength={500} required
                />
                <p className="text-xs text-gray-400 mt-1 text-right">{message.length}/500</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Send to</label>
                <div className="flex gap-2">
                  {[
                    { v: 'all', l: 'All Users', icon: Users },
                    { v: 'business', l: 'Businesses Only', icon: Users },
                    { v: 'wholesaler', l: 'Wholesalers Only', icon: Users }
                  ].map(opt => (
                    <button key={opt.v} type="button" onClick={() => setTargetRole(opt.v)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                        targetRole === opt.v
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}>
                      <opt.icon className="w-4 h-4" /> {opt.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={sending || !title.trim() || !message.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Announcement'}
                </button>
                {sent && (
                  <span className="flex items-center gap-1 text-sm text-green-600">
                    <CheckCircle className="w-4 h-4" /> Sent to {sent.count} users ({sent.role})
                  </span>
                )}
              </div>
            </form>
          </div>

          {/* History */}
          <div className="bg-white rounded-lg border">
            <div className="px-6 py-4 border-b">
              <h2 className="text-sm font-semibold text-gray-900">Recent Announcements</h2>
            </div>
            {loadingHistory ? (
              <div className="p-8 flex justify-center"><LoadingSpinner /></div>
            ) : history.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Megaphone className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                No announcements sent yet
              </div>
            ) : (
              <div className="divide-y">
                {history.map((a, i) => (
                  <div key={i} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900">{a._id}</h3>
                        <p className="text-sm text-gray-600 mt-0.5">{a.message}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {new Date(a.createdAt).toLocaleDateString('el-GR')}
                          </span>
                          <span className="flex items-center gap-1">
                            <Send className="w-3 h-3" /> Sent to {a.count} users
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" /> {a.readCount} read ({a.count > 0 ? Math.round(a.readCount / a.count * 100) : 0}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        icon={confirmModal?.icon}
        confirmLabel={confirmModal?.confirmLabel}
        confirmColor={confirmModal?.confirmColor}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default AdminAnnouncements;